import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#49ffc1' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#49ffc1' },
};

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setValidating(false);
        setTokenValid(false);
        setError('Lien de réinitialisation invalide.');
        return;
      }

      try {
        await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/reset-password/${encodeURIComponent(token)}/validate`,
          { headers: { 'ngrok-skip-browser-warning': '1' } }
        );
        setTokenValid(true);
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Ce lien de réinitialisation est invalide ou a expiré.';
        setError(typeof message === 'string' ? message : 'Lien invalide.');
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    void validate();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/reset-password`,
        { token, password },
        { headers: { 'ngrok-skip-browser-warning': '1' } }
      );
      setSuccess(response.data?.message || 'Mot de passe mis à jour.');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Impossible de réinitialiser le mot de passe.';
      setError(typeof message === 'string' ? message : 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            bgcolor: '#281e30',
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom sx={{ color: '#fff' }} fontWeight="bold">
            Nouveau mot de passe
          </Typography>

          {validating && <CircularProgress sx={{ color: '#49ffc1', my: 4 }} />}

          {!validating && error && !tokenValid && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {!validating && tokenValid && (
            <>
              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                  {success}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Nouveau mot de passe"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading || Boolean(success)}
                  sx={fieldSx}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Confirmer le mot de passe"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading || Boolean(success)}
                  sx={fieldSx}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    bgcolor: '#49ffc1',
                    color: '#281e30',
                    fontWeight: 700,
                    '&:hover': { bgcolor: '#3de6ad' },
                  }}
                  disabled={loading || Boolean(success)}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: '#281e30' }} />
                  ) : (
                    'Enregistrer le mot de passe'
                  )}
                </Button>
              </Box>
            </>
          )}

          {!validating && (
            <Button onClick={() => navigate('/login')} sx={{ color: '#49ffc1', mt: 1 }}>
              Retour à la connexion
            </Button>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
